/**
 * Microsoft Entra ID sign-in — MSAL.js, public client (SPA/PKCE), no secret.
 *
 * Only active when this surface's client id is baked into the build. Leaving it
 * empty makes `entraEnabled` false and `AuthGate` (`../shells/auth-gate`) render
 * straight through to the app, which pairs with the API's own `AUTH_DEV_BYPASS`.
 * Local development normally *does* set it now — both surfaces sign in against
 * real registrations — and a deployed console always does.
 *
 * The API (`apps/api/src/auth/entra.ts`) verifies a bearer access token
 * against Entra's JWKS — it never sees an authorization code and holds no
 * client secret. That is why this is a plain `PublicClientApplication`: the
 * whole exchange (redirect, PKCE, token cache) happens in the browser.
 */

import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';

/**
 * Which console this build is, and therefore which Entra app registration it
 * signs in against. The two are separate registrations — see
 * `apps/api/src/auth/entra.ts` for why the audience is the boundary.
 *
 * The selection happens here rather than in `vite.config.ts`, which is where it
 * started: `define` is not applied to `import.meta.env.*` in dev, because Vite
 * serves that as a real object built from the environment. So a config-level
 * substitution silently did nothing locally and both servers read the same
 * variables — the master console would have offered the client registration.
 * Reading both sets and choosing here works identically in dev and in a build,
 * where the surface is a static string and the dead branch folds away.
 *
 * The client bundle therefore contains the master registration's client id.
 * That is not a secret — a client id appears in every authorize URL — and in a
 * deployed build each image only ever receives its own surface's build args.
 */
const surface = import.meta.env.VITE_CONSOLE_SURFACE === 'master' ? 'master' : 'client';
const isMaster = surface === 'master';

const clientId =
  (isMaster ? import.meta.env.VITE_MASTER_ENTRA_CLIENT_ID : import.meta.env.VITE_ENTRA_CLIENT_ID) ??
  '';
const tenantId =
  (isMaster
    ? (import.meta.env.VITE_MASTER_ENTRA_TENANT_ID ?? import.meta.env.VITE_ENTRA_TENANT_ID)
    : import.meta.env.VITE_ENTRA_TENANT_ID) ?? 'common';
const redirectUri =
  (isMaster
    ? import.meta.env.VITE_MASTER_ENTRA_REDIRECT_URI
    : import.meta.env.VITE_ENTRA_REDIRECT_URI) ?? `${window.location.origin}/auth/callback`;
const apiScope =
  (isMaster ? import.meta.env.VITE_MASTER_ENTRA_API_SCOPE : import.meta.env.VITE_ENTRA_API_SCOPE) ??
  '';

/** Exported so the shells can label which console you are looking at. */
export const consoleSurface = surface;

/** False in every environment that hasn't registered a real Entra app yet. */
export const entraEnabled = clientId !== '';

const scopes = apiScope ? [apiScope] : [];

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    // sessionStorage, not localStorage: a shared/kiosk machine should not
    // keep one person's Otto IQ session alive for the next person to reuse.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = entraEnabled ? new PublicClientApplication(msalConfig) : null;

/**
 * `initialize()` + `handleRedirectPromise()` must both resolve before any
 * other MSAL call — memoized so `AuthGate` can call this from an effect
 * without racing a second mount (React 18 StrictMode double-invokes effects).
 */
let ready: Promise<AccountInfo | null> | null = null;

export function ensureSignedIn(): Promise<AccountInfo | null> {
  if (!msalInstance) return Promise.resolve(null);
  ready ??= (async () => {
    await msalInstance.initialize();
    const redirectResult = await msalInstance.handleRedirectPromise();
    const account = redirectResult?.account ?? msalInstance.getAllAccounts()[0] ?? null;
    if (account) msalInstance.setActiveAccount(account);
    return account;
  })();
  return ready;
}

export function signIn(): void {
  if (!msalInstance) return;
  void msalInstance.loginRedirect({ scopes });
}

export function signOut(): void {
  if (!msalInstance) return;
  void msalInstance.logoutRedirect();
}

/**
 * The `TokenProvider` installed via `setAuthTokenProvider` (`./api.ts`).
 * Tries the cached session first; a token that needs fresh user interaction
 * (consent changed, session expired) falls through to a full redirect rather
 * than throwing — there is no popup fallback, so the current page's request
 * simply never resolves and the browser navigates away instead.
 */
export async function acquireAccessToken(): Promise<string | null> {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ account, scopes });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ account, scopes });
      return null;
    }
    throw error;
  }
}
